import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { LinearProgress } from '@mui/material';
import { debounce } from 'lodash';
import { DomUtils } from '../../utils/dom-utils';
import Icons, { IconSizes } from '../Icons';
import { CustomTooltip } from '../Poppers/CustomTooltip';
import EmptyStateMessage from '../EmptyStateMessage';

import { renderBodyRowProps } from './stories/utils/utils';

import * as S from './styled';

const f = () => {};
const e = {};
const NO_DATA = 'No data found';

export const TableHeader = ({ header, tooltip }) => {
  header = typeof header === 'string' ? header : header?.column?.columnDef?.header;
  const { isCropped, componentRef } = DomUtils.useIsComponentCropped(header);
  const { theme } = DomUtils.useDetectParentTheme(componentRef);
  return (
    <S.HeaderWrapper>
      <CustomTooltip mode={theme} title={isCropped ? header : undefined}>
        <span ref={componentRef}>{header}</span>
      </CustomTooltip>
      {tooltip && <Icons.Info size={IconSizes.S} tooltip={tooltip} />}
    </S.HeaderWrapper>
  );
};
TableHeader.propTypes = {
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ column: PropTypes.shape({}) })])
};

const Table = ({
  data,
  columns,
  fetchNextPage = f,
  isError,
  isLoading,
  isFetching,
  filters = e,
  sorting,
  setSorting = f,
  rowSelection = e,
  setRowSelection = f,
  onSelectAll,
  totalCount,
  bodyRowProps = renderBodyRowProps,
  initialState,
  getRowId,
  resetScrollKey,
  scrollWithinPx = 1500,
  manualSorting = true,
  enableRowSelection = true,
  enableColumnPinning = true,
  filterFromLeafRows,
  enableExpanding,
  getSubRows,
  filterFns,
  globalFilterFn,
  manualFiltering = true,
  enableColumnResizing,
  enableMultiSort,
  dataTestId,
  onRowClick,
  emptyStateMessage = NO_DATA
}) => {
  const tableContainerRef = useRef(null); // we can get access to the underlying TableContainer element and react to its scroll events
  const rowVirtualizerInstanceRef = useRef(null); // we can get access to the underlying Virtualizer instance and call its scrollToIndex method

  // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    debounce(containerRefElement => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        // once the user has scrolled within 1500px (default) of the bottom of the table, fetch more data if we can
        if (scrollHeight - scrollTop - clientHeight < scrollWithinPx && !isFetching && data.length < totalCount) {
          fetchNextPage();
        }
      }
    }, 250),
    [fetchNextPage, isFetching, data.length, totalCount]
  );

  const scrollHandler = event => fetchMoreOnBottomReached(event.target);
  const tableProps = { 'data-testid': dataTestId };
  const wrappedBodyRowProps = useCallback(
    args => {
      const calculatedProps = bodyRowProps(args);
      return {
        'data-testid': `row-${args.row.id}`,
        ...(onRowClick ? { onClick: () => onRowClick(args.row.original), style: { cursor: 'pointer' } } : {}),
        ...calculatedProps
      };
    },
    [bodyRowProps, onRowClick]
  );
  const muiTableBodyCell = useCallback(({ column }) => ({ 'data-testid': `cell-${column.id}` }), []);
  const tableComp = useMaterialReactTable({
    columns,
    data,
    enableExpanding,
    filterFromLeafRows: filterFromLeafRows,
    ...(filterFns ? { filterFns: filterFns } : {}),
    ...(globalFilterFn ? { globalFilterFn } : {}),
    getSubRows: getSubRows,
    enableRowSelection: enableRowSelection,
    enableColumnPinning,
    enableRowVirtualization: true,
    enableColumnResizing: enableColumnResizing,
    enableMultiSort,
    manualFiltering: manualFiltering,
    manualSorting: manualSorting,
    enablePagination: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    autoResetPageIndex: true,
    autoResetAll: true,
    enableSorting: !!sorting,
    onRowSelectionChange: setRowSelection,

    initialState: initialState,
    muiSelectAllCheckboxProps: {
      ...(onSelectAll ? { onChange: onSelectAll } : {}),
      icon: <Icons.Checkbox fontSize={IconSizes.M} />,
      checkedIcon: <Icons.SelectedCheckbox fontSize={IconSizes.M} />,
      indeterminateIcon: <Icons.IndeterminateCheckbox fontSize={IconSizes.M} />
    },
    muiSelectCheckboxProps: {
      icon: <Icons.Checkbox fontSize={IconSizes.M} />,
      checkedIcon: <Icons.SelectedCheckbox fontSize={IconSizes.M} />
    },
    muiExpandAllButtonProps: ({ table }) => ({
      children: table.getIsAllRowsExpanded() ? (
        <Icons.Collapse fontSize={IconSizes.M} rotate={270} />
      ) : (
        <Icons.Collapse fontSize={IconSizes.M} rotate={180} />
      )
    }),
    muiExpandButtonProps: ({ row }) => ({
      // eslint-disable-next-line no-nested-ternary
      children: row.original.subRows?.length ? (
        row.getIsExpanded() ? (
          <Icons.NextArrow fontSize={IconSizes.M} rotate={90} />
        ) : (
          <Icons.NextArrow fontSize={IconSizes.M} />
        )
      ) : (
        <span>&#x2022;</span>
      ) // Render nothing if there are no subRows
    }),
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: { height: '100%' },
      onScroll: scrollHandler
    },
    onSortingChange: setSorting,
    state: {
      globalFilter: filters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      ...(sorting?.length ? { sorting } : {}),
      rowSelection,
      columnOrder: (enableRowSelection ? ['mrt-row-select'] : [])
        .concat(columns.map(col => col.id))
        .concat(['mrt-row-spacer'])
    },
    rowVirtualizerInstanceRef: rowVirtualizerInstanceRef,
    rowVirtualizerProps: { overscan: 4 },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        boxShadow:
          column.getIsPinned() && column.getPinnedIndex() === initialState.columnPinning.left.length - 1
            ? '-5px 0px 5px -6px var(--black-200) inset'
            : '',
        ...(column.getIsPinned()
          ? {}
          : {
              '& .Mui-TableHeadCell-Content-Wrapper': {
                fontSize: '12px',
                fontWeight: 400,
                letterSpacing: '0.5px'
              }
            })
      },
      'data-testid': `column-${column.id}-${dataTestId}`
    }),
    muiTableProps: tableProps,
    muiTableBodyRowProps: wrappedBodyRowProps,
    muiTableBodyCellProps: muiTableBodyCell,
    getRowId
  });

  // scroll to top of table when sorting or filters change
  useEffect(() => {
    // scroll to the top of the table when the sorting changes
    try {
      if (data.length) {
        rowVirtualizerInstanceRef.current?.scrollToOffset?.(0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [sorting, filters, resetScrollKey, !!data.length]);

  return (
    <S.TableContainer>
      {isFetching && <LinearProgress color="success" />}
      {data.length === 0 && !isFetching ? (
        <S.EmptyStateContainer>
          <EmptyStateMessage message={emptyStateMessage} />
        </S.EmptyStateContainer>
      ) : (
        <S.TableWrapper className="dsc-table-wrapper">
          <S.Container className="dsc-table-container" $enablePagination={!!initialState?.enableBottomToolbar}>
            <MaterialReactTable table={tableComp} />
          </S.Container>
        </S.TableWrapper>
      )}
    </S.TableContainer>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  fetchNextPage: PropTypes.func,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  totalCount: PropTypes.number.isRequired,
  filters: PropTypes.object,
  sorting: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  ),
  setSorting: PropTypes.func,
  bodyRowProps: PropTypes.func,
  onSelectAll: PropTypes.func,
  initialState: PropTypes.object.isRequired,
  getRowId: PropTypes.func.isRequired,
  resetScrollKey: PropTypes.string,
  scrollWithinPx: PropTypes.number,
  manualSorting: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  enableColumnPinning: PropTypes.bool,
  enableColumnResizing: PropTypes.bool,
  manualFiltering: PropTypes.bool,
  enableMultiSort: PropTypes.bool,
  dataTestId: PropTypes.string.isRequired,
  onRowClick: PropTypes.func,
  emptyStateMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

export default Table;
